import { useState, useEffect } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import 'react-perfect-scrollbar/dist/css/styles.css';
import { chain } from 'lodash';
import { getYear, startOfYear, endOfYear, subYears, isAfter, isBefore, isEqual } from 'date-fns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Box,
} from '@mui/material';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import theme from 'theme';
import { Portlet, PortletHeader, PortletLabel, PortletContent } from '../../components/Portlets';
import { Status } from '../../components/Status';
import { Cell } from '../../components/Cell';
import { formatDate, snapToArray, setDateHourToMidnight } from '../../utils';
import { STATUS_COLORS } from '../../constants/requestStatus';
import { useFirestore } from '../../utils/firebase';
import { useAuth } from '../../auth';
import RequestAction from './RequestAction';
import ResolutionCell from './ResolutionCell';
import Controls from './Controls';
import { apiClient } from '../../api-client';
import { createTempoWorklog } from '../../utils/functions/tempoHelpers';
import { updateVacationRequestStatus } from '../../services/vacationService';

const TeamVacationRequestsTable = () => {
  const { user: authenticatedUser, getToken } = useAuth();

  const [isLoading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState(['pending']);
  const [nameFilter, setNameFilter] = useState('');
  const [vacationPeriod, setVacationPeriod] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const database = useFirestore();

  const filterRequests = requests => {
    if (!requests) {
      return null;
    }

    const filterByName = ({ requestedBy: { firstName, lastName } }) => {
      const fields = [firstName, lastName, `${firstName} ${lastName}`];

      return fields.some(string => string.toLowerCase().startsWith(nameFilter.toLowerCase()));
    };

    const filterByPeriod = ({ vacationFrom, vacationTo }) => {
      if (!vacationPeriod) {
        return true;
      }

      const fromDate = vacationFrom?.toDate ? vacationFrom.toDate() : new Date(vacationFrom);
      const toDate = vacationTo?.toDate ? vacationTo.toDate() : new Date(vacationTo);
      const { start, end } = vacationPeriod;

      return isAfter(fromDate, start) && (isBefore(toDate, end) || isEqual(toDate, end));
    };

    return chain(requests).filter(filterByName).filter(filterByPeriod).value();
  };

  const getVacationPeriods = years => {
    const year = getYear(new Date());
    const baseStart = startOfYear(new Date(year, 0, 1));
    const baseEnd = endOfYear(new Date(year, 0, 1));

    return Array.from({ length: years }, (_, key) => {
      const start = subYears(baseStart, key);
      const end = subYears(baseEnd, key);
      return { start, end, key };
    });
  };

  const formatRequestedBy = ({ firstName, lastName } = {}) =>
    firstName && lastName ? (
      <div>
        {firstName} {lastName}
      </div>
    ) : (
      <div>{firstName || lastName}</div>
    );

  const updateRequests = input => {
    setRequests(prev =>
      prev.map(request => (request.id === input.requestId ? { ...request, ...input } : request)),
    );
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteEvent = async (eventFrom, eventTo, userId) => {
    const url = '/calendar/event';

    const body = {
      calendarType: 'vacation',
      title: 'Vacation',
      eventFrom: setDateHourToMidnight(eventFrom.toDate()),
      eventTo: setDateHourToMidnight(eventTo.toDate()),
      userId,
      tenantId: authenticatedUser.tenant.id,
    };

    const response = await apiClient('DELETE', url, getToken, body);
    if (response.statusCode > 204) {
      throw response;
    }
  };

  const onApproveRequestAction = async ({
    requestId,
    status,
    requestedBy,
    vacationFrom,
    vacationTo,
  }) => {
    const { didUpdate } = await updateVacationRequestStatus(getToken, requestId, status);

    if (!didUpdate) return;

    updateRequests({ requestId, status });

    createTempoWorklog(getToken, requestedBy.email, vacationFrom, vacationTo, 'vacation');
  };

  const onDenyRequestAction = async ({ requestId, status, note }) => {
    const { didUpdate } = await updateVacationRequestStatus(getToken, requestId, status, note);

    if (!didUpdate) return;

    updateRequests({ requestId, status });
  };

  const onCancelRequestAction = async ({ requestId, userId, status, startDate, endDate }) => {
    const { didUpdate } = await updateVacationRequestStatus(getToken, requestId, status);

    if (!didUpdate) return;

    await handleDeleteEvent(startDate, endDate, userId);
    updateRequests({ requestId, status });
  };
  const onGenerateResolutionAction = async ({ requestId, registryNumber }) => {
    const response = await apiClient('POST', '/vacation/resolution/generate', getToken, {
      requestId,
      registryNumber,
    });
    if (!response?.pdfs) throw response;
    updateRequests({
      requestId,
      resolution: { status: 'pending_review', registryNumber, pdfs: response.pdfs },
    });
    return response;
  };

  const onSendResolutionAction = async ({ requestId, registryNumber }) => {
    const response = await apiClient('POST', '/vacation/resolution/send', getToken, { requestId });
    if (!response.mailDocId) throw response;
    setRequests(prev =>
      prev.map(request =>
        request.id === requestId
          ? {
              ...request,
              resolution: {
                ...request.resolution,
                registryNumber,
                mailDocId: response.mailDocId,
              },
            }
          : request,
      ),
    );
    return response;
  };

  const hideActionsColumnAndCell = filteredRequests =>
    filteredRequests.filter(
      request => request.status === 'pending' || request.status === 'approved',
    ).length > 0;

  useEffect(() => {
    setLoading(true);
    let queryRequests = query(
      collection(database, 'vacation_requests'),
      where('userTenant', '==', authenticatedUser.tenant.id),
      orderBy('requestedAt', 'desc'),
    );

    if (statusFilter.length !== 0) {
      queryRequests = query(
        collection(database, 'vacation_requests'),
        where('userTenant', '==', authenticatedUser.tenant.id),
        where('status', 'in', statusFilter),
        orderBy('requestedAt', 'desc'),
      );
    }

    getDocs(queryRequests)
      .then(snapshot => {
        setRequests(snapToArray(snapshot));
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [database, statusFilter, page, rowsPerPage, authenticatedUser.tenant.id]);

  const filteredRequests = filterRequests(requests);
  const isAccountManager = authenticatedUser.roles?.accountManager === true;
  const { assignedManager } = authenticatedUser;
  const isOwnAssignedManager = Array.isArray(assignedManager)
    ? assignedManager.some(manager => manager.email === authenticatedUser.email)
    : assignedManager?.email === authenticatedUser.email;

  const canApproveOrDeny = (requestUserId, status) => {
    if (status !== 'pending' && status !== 'approved') return false;
    const isOwnPendingRequest = status === 'pending' && requestUserId === authenticatedUser.uid;
    return !isOwnPendingRequest || isOwnAssignedManager;
  };

  return (
    <Portlet
      sx={{ background: 'transparent', border: 'none', boxShadow: 'none' }}
      outlined={1}
      squared={false}
    >
      <PortletHeader
        sx={{
          '@media (max-width:480px)': {
            display: 'table',
            paddingBottom: '1.5rem',
          },
        }}
        noDivider
      >
        <PortletLabel
          subtitle={`${filteredRequests.length} in total`}
          title="Latest Requests"
          sx={{
            '@media (max-width:480px)': {
              '& h5': {
                fontSize: '1.3rem',
              },
              '& h6': {
                marginLeft: '0.5rem',
                fontSize: '0.7rem',
                width: '3rem',
              },
            },
          }}
        />

        <Controls
          periods={getVacationPeriods(2)}
          statusFilter={statusFilter}
          nameFilter={nameFilter}
          setNameFilter={setNameFilter}
          setStatusFilter={setStatusFilter}
          vacationPeriod={vacationPeriod}
          setVacationPeriod={setVacationPeriod}
          setPage={setPage}
        />
      </PortletHeader>

      <PerfectScrollbar>
        <PortletContent isLoading={isLoading} noPadding>
          {filteredRequests.length ? (
            <>
              <Table
                sx={{
                  '& th': {
                    whiteSpace: 'nowrap',
                  },
                  '& td, & th': {
                    paddingLeft: '12px',
                    paddingRight: '12px',
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Requested by</TableCell>
                    <TableCell
                      style={isAccountManager ? { width: 60, whiteSpace: 'normal' } : undefined}
                    >
                      Duration
                    </TableCell>
                    <Tooltip title="Number of available vacation days at the moment of request creation.">
                      <TableCell>Available days</TableCell>
                    </Tooltip>
                    <TableCell>Requested at</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell>To</TableCell>
                    {!isAccountManager && <TableCell>Description</TableCell>}
                    {!isAccountManager &&
                      (['pending', 'all', 'approved'].some(item => statusFilter.includes(item)) ||
                        statusFilter.length === 0) &&
                      hideActionsColumnAndCell(filteredRequests) && <TableCell>Actions</TableCell>}
                    {isAccountManager && (
                      <TableCell colSpan={3}>Decision on annual leave</TableCell>
                    )}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredRequests
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map(
                      ({
                        id: requestId,
                        userId,
                        requestedBy = {},
                        description,
                        noOfDays,
                        requestedAt,
                        vacationFrom,
                        vacationTo,
                        status = 'pending',
                        userAvailableDays,
                        userOldVacationDays,
                        resolution,
                      }) => (
                        <TableRow
                          sx={{ cursor: 'pointer' }}
                          hover
                          key={requestId}
                          data-cy={description}
                        >
                          <Cell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Status
                                sx={{ marginRight: theme.spacing(1) }}
                                color={STATUS_COLORS[status]}
                                size="sm"
                              />
                              {status}
                            </Box>
                          </Cell>
                          <Cell>{formatRequestedBy(requestedBy)}</Cell>
                          <Cell>{noOfDays}</Cell>
                          <Cell>
                            <Tooltip title="The number of available vacation days at the moment of request creation.">
                              <div>{(userAvailableDays || 0) + (userOldVacationDays || 0)}</div>
                            </Tooltip>
                          </Cell>
                          <Cell>{formatDate(requestedAt, 'MMMM dd, yyyy')}</Cell>
                          <Cell>{formatDate(vacationFrom, 'MMMM dd, yyyy')}</Cell>
                          <Cell>{formatDate(vacationTo, 'MMMM dd, yyyy')}</Cell>
                          {!isAccountManager && (
                            <TableCell
                              sx={{
                                fontWeight: 400,
                                maxWidth: 300,
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                              }}
                            >
                              {description || '-'}
                            </TableCell>
                          )}
                          {!isAccountManager && hideActionsColumnAndCell(filteredRequests) && (
                            <Cell>
                              {canApproveOrDeny(userId, status) ? (
                                <RequestAction
                                  userId={userId}
                                  noOfDays={+noOfDays}
                                  requestId={requestId}
                                  status={status}
                                  requestedBy={requestedBy}
                                  vacationFrom={vacationFrom}
                                  vacationTo={vacationTo}
                                  onApproveRequestAction={onApproveRequestAction}
                                  onDenyRequestAction={onDenyRequestAction}
                                  onCancelRequestAction={onCancelRequestAction}
                                  description={description}
                                />
                              ) : (
                                <div />
                              )}
                            </Cell>
                          )}
                          {isAccountManager && (
                            <ResolutionCell
                              resolution={resolution}
                              status={status}
                              requestId={requestId}
                              requestedBy={requestedBy}
                              vacationFrom={vacationFrom}
                              vacationTo={vacationTo}
                              onGenerateResolutionAction={onGenerateResolutionAction}
                              onSendResolutionAction={onSendResolutionAction}
                            />
                          )}
                        </TableRow>
                      ),
                    )}
                </TableBody>
              </Table>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                onRowsPerPageChange={handleChangeRowsPerPage}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                component="div"
                count={filteredRequests.length}
                page={page}
              />
            </>
          ) : (
            <Box sx={{ padding: 24, paddingTop: 0, fontWeight: 300, color: '#777' }}>
              No requests found
            </Box>
          )}
        </PortletContent>
      </PerfectScrollbar>
    </Portlet>
  );
};

export default TeamVacationRequestsTable;
